import { useFetcher } from '@remix-run/react';
import { Dialog } from 'radix-ui';
import React, { useState, useRef } from 'react';

import type { NetworkInvitee } from '~/services/network-invitations.server';

import { Button } from '../ui/Button';

type ModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

type TabType = 'copy' | 'manual' | 'csv';

interface ManualInvitee {
  name: string;
  email: string;
}

type NetworkInvitationResponse =
  | { error: string }
  | {
      success: true;
      result: {
        sent: number;
        skipped: number;
        failed: number;
        skippedEmails: string[];
        failedEmails: string[];
      };
    };

type NetworkInvitationTemplateResponse =
  | { error: string }
  | {
      success: true;
      template: {
        senderName: string;
        senderOfficeName: string | null;
        signupUrl: string;
      };
    };

/**
 * Simple CSV parser for client-side use
 */
function parseCSV(content: string): NetworkInvitee[] {
  const lines = content.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];

  // Try to detect if first line is a header
  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes('name') && firstLine.includes('email');

  const startIndex = hasHeader ? 1 : 0;
  const invitees: NetworkInvitee[] = [];

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Simple CSV parsing (handles quoted values)
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());

    // Expect name,email format
    if (values.length >= 2) {
      const name = values[0].replace(/^"|"$/g, '').trim();
      const email = values[1].replace(/^"|"$/g, '').trim();

      if (name && email) {
        invitees.push({ name, email });
      }
    }
  }

  return invitees;
}

type NetworkInvitationTemplateFields = {
  signupUrl: string;
  senderName: string;
  senderOfficeName: string | null;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain text plus HTML for rich paste (Outlook-style bold, bullets, link, signature). */
function buildNetworkInvitationCopy(template: NetworkInvitationTemplateFields): {
  plain: string;
  html: string;
} {
  const { signupUrl, senderName, senderOfficeName } = template;
  const urlSafe = escapeHtml(signupUrl);
  const nameSafe = escapeHtml(senderName);
  const officeSafe = senderOfficeName ? escapeHtml(senderOfficeName) : null;

  const plain = `Hi [Name],

I've been using ToothReferral to manage my referrals and networking, and it has been a game-changer for communicating with other offices in a centralized and secure way. I thought it might be a valuable tool for your practice as well.

You can create your free account here (sign-up takes less than 60 seconds):

${signupUrl}

The platform makes it much easier for us to stay in sync:

• Direct Collaboration: We can message each other directly about specific referrals within the cloud-based platform, keeping all communication organized right over the referral itself.

• Centralized & Secure: It keeps all our case-related discussions in one secure location, ensuring patient data remains protected.

• Real-Time Access: If you need assistance with one of your patients, or if I have a question regarding a case, we can connect quickly and securely through the app.

• SEO & Marketing: The platform also offers free marketing and SEO opportunities to help your general practice get discovered by new patients and referral sources—which I have already paid for your practice to use free of charge!

Once you're set up, I'll automatically appear as a default favorite specialist in your search, making it easy for us to coordinate on patient care.

Best regards,
${senderName}${senderOfficeName ? `\n${senderOfficeName}` : ''}`;

  const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:#333333;">
<p style="margin:0 0 16px 0;">Hi [Name],</p>
<p style="margin:0 0 16px 0;">I've been using <strong>ToothReferral</strong> to manage my referrals and networking, and it has been a game-changer for communicating with other offices in a centralized and secure way. I thought it might be a valuable tool for your practice as well.</p>
<p style="margin:0 0 8px 0;"><strong>You can create your free account here (sign-up takes less than 60 seconds):</strong></p>
<p style="margin:0 0 16px 0;"><a href="${urlSafe}" style="color:#0563C1;text-decoration:underline;">${urlSafe}</a></p>
<p style="margin:0 0 8px 0;">The platform makes it much easier for us to stay in sync:</p>
<ul style="margin:0 0 16px 0;padding-left:1.5em;">
<li style="margin-bottom:10px;"><strong>Direct Collaboration:</strong> We can message each other directly about specific referrals within the cloud-based platform, keeping all communication organized right over the referral itself.</li>
<li style="margin-bottom:10px;"><strong>Centralized &amp; Secure:</strong> It keeps all our case-related discussions in one secure location, ensuring patient data remains protected.</li>
<li style="margin-bottom:10px;"><strong>Real-Time Access:</strong> If you need assistance with one of your patients, or if I have a question regarding a case, we can connect quickly and securely through the app.</li>
<li style="margin-bottom:0;"><strong>SEO &amp; Marketing:</strong> The platform also offers free marketing and SEO opportunities to help your general practice get discovered by new patients and referral sources—<strong>which I have already paid for your practice to use free of charge!</strong></li>
</ul>
<p style="margin:0 0 16px 0;">Once you're set up, I'll automatically appear as a default favorite specialist in your search, making it easy for us to coordinate on patient care.</p>
<p style="margin:0;">Best regards,</p>
<p style="margin:8px 0 0 0;"><strong>${nameSafe}</strong>${officeSafe ? `<br><em>${officeSafe}</em>` : ''}</p>
</div>`;

  return { plain, html };
}

export function NetworkInviteModal({ isOpen, setIsOpen }: ModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('copy');
  const [manualInvitees, setManualInvitees] = useState<ManualInvitee[]>([{ name: '', email: '' }]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<NetworkInvitee[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [result, setResult] = useState<{
    sent: number;
    skipped: number;
    failed: number;
    skippedEmails: string[];
    failedEmails: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fetcher = useFetcher<NetworkInvitationResponse>();
  const templateFetcher = useFetcher<NetworkInvitationTemplateResponse>();

  const isLoading = fetcher.state === 'submitting' || fetcher.state === 'loading';
  const isTemplateLoading =
    templateFetcher.state === 'submitting' || templateFetcher.state === 'loading';

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setManualInvitees([{ name: '', email: '' }]);
      setCsvFile(null);
      setCsvPreview([]);
      setError(null);
      setCopySuccess(null);
      setResult(null);
      setActiveTab('copy');
    }
  }, [isOpen]);

  // Handle fetcher response
  React.useEffect(() => {
    if (fetcher.data) {
      if ('error' in fetcher.data) {
        setError(fetcher.data.error);
      } else if ('success' in fetcher.data && fetcher.data.success && fetcher.data.result) {
        setResult(fetcher.data.result);
        // Clear form on success
        if (activeTab === 'manual') {
          setManualInvitees([{ name: '', email: '' }]);
        } else {
          setCsvFile(null);
          setCsvPreview([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    }
  }, [fetcher.data, activeTab]);

  React.useEffect(() => {
    if (!isOpen || activeTab !== 'copy') return;
    if (templateFetcher.data && 'success' in templateFetcher.data) return;
    templateFetcher.load('/api/network-invitations?mode=template');
  }, [activeTab, isOpen, templateFetcher]);

  React.useEffect(() => {
    if (!templateFetcher.data) return;
    if ('error' in templateFetcher.data) {
      setError(templateFetcher.data.error);
    } else {
      setError(null);
    }
  }, [templateFetcher.data]);

  const invitationTemplate =
    templateFetcher.data && 'success' in templateFetcher.data
      ? templateFetcher.data.template
      : null;

  const invitationCopy = React.useMemo(
    () => (invitationTemplate ? buildNetworkInvitationCopy(invitationTemplate) : null),
    [invitationTemplate]
  );

  const copyTemplate = async () => {
    if (!invitationCopy) return;
    const { plain, html } = invitationCopy;
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([plain], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      setCopySuccess(
        'Invitation copied (bold headings, bullets, and link styling when pasted into email or Word).'
      );
    } catch {
      try {
        await navigator.clipboard.writeText(plain);
        setCopySuccess('Invitation template copied to clipboard.');
      } catch {
        setError('Failed to copy template. Please copy manually.');
      }
    }
  };

  const rotateTemplateLink = () => {
    setCopySuccess(null);
    setError(null);
    const formData = new FormData();
    formData.append('action', 'rotate-template-link');
    templateFetcher.submit(formData, { method: 'POST', action: '/api/network-invitations' });
  };

  const handleManualChange = (index: number, field: 'name' | 'email', value: string) => {
    const updated = [...manualInvitees];
    updated[index] = { ...updated[index], [field]: value };
    setManualInvitees(updated);
    setError(null);
  };

  const addManualRow = () => {
    setManualInvitees([...manualInvitees, { name: '', email: '' }]);
  };

  const removeManualRow = (index: number) => {
    if (manualInvitees.length > 1) {
      setManualInvitees(manualInvitees.filter((_, i) => i !== index));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setError(null);
    setResult(null);

    const reader = new FileReader();
    reader.onload = event => {
      const content = event.target?.result as string;
      const parsed = parseCSV(content);
      if (parsed.length === 0) {
        setError('No valid entries found in CSV file. Expected format: name,email');
      } else {
        setCsvPreview(parsed);
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csvContent = 'name,email\nJohn Doe,john@example.com\nJane Smith,jane@example.com';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'network_invitation_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    let invitees: NetworkInvitee[] = [];

    if (activeTab === 'manual') {
      // Filter out empty rows
      invitees = manualInvitees
        .filter(inv => inv.name.trim() && inv.email.trim())
        .map(inv => ({ name: inv.name.trim(), email: inv.email.trim() }));
    } else {
      invitees = csvPreview;
    }

    if (invitees.length === 0) {
      setError('Please add at least one invitee');
      return;
    }

    // Submit via fetcher using FormData (Remix pattern)
    const formData = new FormData();
    formData.append('invitees', JSON.stringify(invitees));

    fetcher.submit(formData, {
      method: 'POST',
      action: '/api/network-invitations',
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={`fixed inset-0 bg-black/40 ${isLoading ? 'cursor-wait' : ''}`} />
        <Dialog.Content
          style={{ zIndex: 99 }}
          className={`fixed left-1/2 top-1/2 max-h-[90vh] w-[95vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-6 shadow-lg focus:outline-none overflow-y-auto ${isLoading ? 'pointer-events-none' : ''}`}
        >
          <Dialog.Title className="text-xl font-semibold mb-2">Invite your Network</Dialog.Title>
          <Dialog.Description className="mb-4 text-gray-600">
            Invite multiple people from your referral network to join ToothReferral. When they sign
            up, each invitee will have you as a favorite, making it easy for them to find and refer
            to you.
          </Dialog.Description>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab('copy')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'copy'
                  ? 'border-b-2 border-purple-500 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              disabled={isLoading || isTemplateLoading}
            >
              Copy Template
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('manual')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'manual'
                  ? 'border-b-2 border-purple-500 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              disabled={isLoading}
            >
              Manual Entry
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('csv')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'csv'
                  ? 'border-b-2 border-purple-500 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              disabled={isLoading}
            >
              Upload CSV
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {activeTab === 'copy' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  Copy this invitation and send it from your personal/work email. The same link can
                  be shared with multiple recipients. The preview below matches how Gmail, Outlook,
                  and Word usually render the message after you paste; use the copy button rather
                  than selecting from the preview for best results.
                </div>
                {isTemplateLoading || !invitationCopy ? (
                  <div className="flex min-h-[320px] w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-500">
                    {isTemplateLoading ? 'Generating invitation template...' : 'Loading...'}
                  </div>
                ) : (
                  <div
                    className="max-h-[min(480px,55vh)] min-h-[320px] w-full overflow-y-auto rounded-lg border border-gray-200 bg-white px-4 py-3 text-left"
                    // Built in-app from static markup plus escapeHtml() for URL and signature fields.
                    dangerouslySetInnerHTML={{ __html: invitationCopy.html }}
                  />
                )}
                <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                  <Button
                    type="button"
                    onClick={copyTemplate}
                    disabled={!invitationCopy || isTemplateLoading}
                  >
                    {isTemplateLoading ? 'Preparing...' : 'Copy Invitation Template'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={rotateTemplateLink}
                    disabled={isTemplateLoading}
                  >
                    Rotate Link
                  </Button>
                </div>
                {copySuccess && <div className="text-green-700 text-sm">{copySuccess}</div>}
              </div>
            )}

            {/* Manual Entry Tab */}
            {activeTab === 'manual' && (
              <div className="space-y-4">
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Name
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Email
                        </th>
                        <th className="px-4 py-2 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualInvitees.map((invitee, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              placeholder="Full name"
                              className="w-full bg-gray-50 border border-gray-200 text-gray-900 px-3 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              value={invitee.name}
                              onChange={e => handleManualChange(index, 'name', e.target.value)}
                              disabled={isLoading}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="email"
                              placeholder="email@example.com"
                              className="w-full bg-gray-50 border border-gray-200 text-gray-900 px-3 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              value={invitee.email}
                              onChange={e => handleManualChange(index, 'email', e.target.value)}
                              disabled={isLoading}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            {manualInvitees.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeManualRow(index)}
                                className="text-red-600 hover:text-red-800"
                                disabled={isLoading}
                              >
                                ×
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={addManualRow}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                  disabled={isLoading}
                >
                  + Add Another
                </button>
              </div>
            )}

            {/* CSV Upload Tab */}
            {activeTab === 'csv' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-purple-600 hover:text-purple-800 font-medium"
                    disabled={isLoading}
                  >
                    Click to upload CSV file
                  </button>
                  <p className="text-sm text-gray-600 mt-2">or drag and drop your CSV file here</p>
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className="text-sm text-gray-500 hover:text-gray-700 underline mt-2"
                    disabled={isLoading}
                  >
                    Download template
                  </button>
                </div>

                {csvPreview.length > 0 && (
                  <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                    <div className="bg-gray-50 px-4 py-2 font-medium text-sm text-gray-700">
                      Preview ({csvPreview.length} contacts)
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                            Email
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((invitee, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-4 py-2 text-sm">{invitee.name}</td>
                            <td className="px-4 py-2 text-sm">{invitee.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && <div className="text-red-600 text-sm mt-4">{error}</div>}

            {/* Result Summary */}
            {result && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-2">Invitation Summary</h3>
                <ul className="text-sm space-y-1">
                  <li className="text-green-600">✓ {result.sent} invitations sent</li>
                  {result.skipped > 0 && (
                    <li className="text-yellow-600">
                      ⚠ {result.skipped} skipped (duplicates or already onboarded)
                    </li>
                  )}
                  {result.failed > 0 && (
                    <li className="text-red-600">✗ {result.failed} failed to send</li>
                  )}
                </ul>
                {result.skippedEmails.length > 0 && (
                  <details className="mt-2 text-xs text-gray-600">
                    <summary className="cursor-pointer">Skipped emails</summary>
                    <ul className="mt-1 list-disc list-inside">
                      {result.skippedEmails.slice(0, 10).map((email, i) => (
                        <li key={i}>{email}</li>
                      ))}
                      {result.skippedEmails.length > 10 && (
                        <li>... and {result.skippedEmails.length - 10} more</li>
                      )}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-6">
              <Dialog.Close asChild>
                <Button variant="secondary" disabled={isLoading}>
                  Close
                </Button>
              </Dialog.Close>
              {activeTab !== 'copy' && (
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Invitations'}
                </Button>
              )}
            </div>
          </form>

          <Dialog.Close asChild>
            <button
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-700"
              aria-label="Close"
              type="button"
              disabled={isLoading}
            >
              ×
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
